import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { Toaster } from 'react-hot-toast';

// Context providers
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';

// Components
import Layout from './components/Layout/Layout';
import theme from './theme';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import PartnerPortalShell from './components/PartnerPortal/PartnerPortalShell';
import PartnerProtectedRoute from './components/PartnerPortal/PartnerProtectedRoute';
import PartnerLayout from './components/PartnerPortal/PartnerLayout';
import ClientPortalShell from './components/ClientPortal/ClientPortalShell';
import ClientProtectedRoute from './components/ClientPortal/ClientProtectedRoute';
import ClientLayout from './components/ClientPortal/ClientLayout';

// Pages
import LoginPage from './pages/Auth/LoginPage';
import SignupPage from './pages/Auth/SignupPage';
import AdminSignupPage from './pages/Auth/AdminSignupPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import BrandSelectorPage from './pages/Brands/BrandSelectorPage';
import BrandDashboardPage from './pages/Brands/BrandDashboardPage';
import BrandDropPage from './pages/Brands/BrandDropPage';
import BrandCalendarPage from './pages/Brands/BrandCalendarPage';
import AddBrandPage from './pages/Brands/AddBrandPage';
import CampaignsPage from './pages/Campaigns/CampaignsPage';
import CampaignDetailPage from './pages/Campaigns/CampaignDetailPage';
import CampaignBuilderPage from './pages/Campaigns/CampaignBuilderPage';
import DropDashboardPage from './pages/Campaigns/DropDashboardPage';
import AnalyticsPage from './pages/Analytics/AnalyticsPage';
import SettingsPage from './pages/Settings/SettingsPage';
import PartnersPage from './pages/Partners/PartnersPage';
import AddPartnerPage from './pages/Partners/AddPartnerPage';
import PartnerApplicationDetailPage from './pages/Partners/PartnerApplicationDetailPage';
import WholesaleSuppliersPage from './pages/Partners/WholesaleSuppliersPage';
import ProductionPartnersPage from './pages/Partners/ProductionPartnersPage';
import CreateProductPage from './pages/Design/CreateProductPage';
import PromptGeneratorPage from './pages/Design/PromptGeneratorPage';
import PatternLibraryPage from './pages/Design/PatternLibraryPage';
import PatternManagementPage from './pages/Design/PatternManagementPage';
import DesignStudioPage from './pages/Design/DesignStudioPage';
import GarmentDesignerPage from './pages/Design/GarmentDesignerPage';
import JournalPage from './pages/JournalPage';
import PartnerDetailPage from './pages/Partners/PartnerDetailPage';
import StorefrontExportPage from './pages/StorefrontExport/StorefrontExportPage';
import NotFoundPage from './pages/NotFoundPage';
import PartnerLoginPage from './pages/PartnerPortal/PartnerLoginPage';
import PartnerDashboardPage from './pages/PartnerPortal/PartnerDashboardPage';
import PartnerOrdersPage from './pages/PartnerPortal/PartnerOrdersPage';
import PartnerProfilePage from './pages/PartnerPortal/PartnerProfilePage';
import PartnerRFQListPage from './pages/PartnerPortal/PartnerRFQListPage';
import PartnerRFQDetailPage from './pages/PartnerPortal/PartnerRFQDetailPage';
import ClientLoginPage from './pages/ClientPortal/ClientLoginPage';
import ClientDashboardPage from './pages/ClientPortal/ClientDashboardPage';
import ClientCampaignsPage from './pages/ClientPortal/ClientCampaignsPage';
import ClientCampaignDetailPage from './pages/ClientPortal/ClientCampaignDetailPage';
import ClientApprovalsPage from './pages/ClientPortal/ClientApprovalsPage';
import ClientLooksPage from './pages/ClientPortal/ClientLooksPage';
import ClientCollectionPage from './pages/ClientPortal/ClientCollectionPage';
import ClientDirectionPage from './pages/ClientPortal/ClientDirectionPage';
import ClientTimelinePage from './pages/ClientPortal/ClientTimelinePage';
import ClientAgreementPage from './pages/ClientPortal/ClientAgreementPage';
import ClientSalesPage from './pages/ClientPortal/ClientSalesPage';
import ClientDropsPage from './pages/ClientPortal/ClientDropsPage';
import ClientAccountPage from './pages/ClientPortal/ClientAccountPage';
import RFQListPage from './pages/RFQ/RFQListPage';
import CreateRFQPage from './pages/RFQ/CreateRFQPage';
import RFQDetailPage from './pages/RFQ/RFQDetailPage';
import PartnerQuotesDemo from './pages/PartnerQuotesDemo';
import GetStartedPage from './pages/Onboarding/GetStartedPage';
import MeasurementProfilesPage from './pages/Customers/MeasurementProfilesPage';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SocketProvider>
            <Router>
              <div className="App">
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/admin-signup" element={<AdminSignupPage />} />
                  <Route path="/partner-quotes-demo" element={<PartnerQuotesDemo />} />
                  <Route path="/partner" element={<PartnerPortalShell />}>
                    <Route path="login" element={<PartnerLoginPage />} />
                    <Route
                      path=""
                      element={
                        <PartnerProtectedRoute>
                          <PartnerLayout />
                        </PartnerProtectedRoute>
                      }
                    >
                      <Route index element={<Navigate to="dashboard" replace />} />
                      <Route path="dashboard" element={<PartnerDashboardPage />} />
                      <Route path="rfqs" element={<PartnerRFQListPage />} />
                      <Route path="rfqs/:rfqId" element={<PartnerRFQDetailPage />} />
                      <Route path="orders" element={<PartnerOrdersPage />} />
                      <Route path="profile" element={<PartnerProfilePage />} />
                    </Route>
                  </Route>
                  <Route path="/client-portal" element={<ClientPortalShell />}>
                    <Route index element={<Navigate to="login" replace />} />
                    <Route path="login" element={<ClientLoginPage />} />
                    <Route element={<ClientProtectedRoute />}>
                      <Route element={<ClientLayout />}>
                        <Route index element={<Navigate to="dashboard" replace />} />
                        <Route path="dashboard" element={<ClientDashboardPage />} />
                        <Route path="campaigns" element={<ClientCampaignsPage />} />
                        <Route path="campaigns/:id" element={<ClientCampaignDetailPage />} />
                        <Route path="looks" element={<ClientLooksPage />} />
                        <Route path="collection" element={<ClientCollectionPage />} />
                        <Route path="sales" element={<ClientSalesPage />} />
                        <Route path="performance" element={<ClientSalesPage />} />
                        <Route path="drops" element={<ClientDropsPage />} />
                        <Route path="approvals" element={<ClientApprovalsPage />} />
                        <Route path="direction" element={<ClientDirectionPage />} />
                        <Route path="timeline" element={<ClientTimelinePage />} />
                        <Route path="agreement" element={<ClientAgreementPage />} />
                        <Route path="account" element={<ClientAccountPage />} />
                      </Route>
                    </Route>
                  </Route>
                  
                  {/* Onboarding route (protected but skips onboarding check) */}
                  <Route
                    path="/get-started"
                    element={
                      <ProtectedRoute skipOnboardingCheck>
                        <GetStartedPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Protected routes with layout */}
                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Layout />
                      </ProtectedRoute>
                    }
                  >
                    {/* Dashboard routes */}
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    
                    {/* Brand management routes */}
                    <Route path="brands" element={<BrandSelectorPage />} />
                    <Route path="brands/new" element={<AddBrandPage />} />
                    <Route path="brands/:brandId" element={<BrandDashboardPage />} />
                    <Route path="brands/:brandId/planning/drop" element={<BrandDropPage />} />
                    <Route path="brands/:brandId/planning/calendar" element={<BrandCalendarPage />} />
                    <Route path="brands/:brandId/design/create-product" element={<CreateProductPage />} />
                    <Route path="brands/:brandId/design/prompt-generator" element={<PromptGeneratorPage />} />
                    <Route path="brands/:brandId/design/pattern-library" element={<PatternLibraryPage />} />
                    <Route path="brands/:brandId/design/patterns" element={<PatternManagementPage />} />
                    <Route path="brands/:brandId/design/studio" element={<DesignStudioPage />} />
                    <Route path="brands/:brandId/journal" element={<JournalPage />} />

                    {/* Storefront Export */}
                    <Route path="storefront-export" element={<StorefrontExportPage />} />

                    {/* Garment Designer (standalone) */}
                    <Route path="design/garment-designer" element={<GarmentDesignerPage />} />

                    {/* Campaign management routes */}
                    <Route path="campaigns" element={<CampaignsPage />} />
                    <Route path="campaigns/new" element={<CampaignBuilderPage />} />
                    <Route path="campaigns/:campaignId/edit" element={<CampaignBuilderPage />} />
                    <Route path="campaigns/:campaignId/details" element={<CampaignDetailPage />} />
                    <Route path="brands/:brandId/campaigns/:campaignId/run" element={<DropDashboardPage />} />
                    
                    {/* Analytics routes */}
                    <Route path="analytics" element={<AnalyticsPage />} />
                    <Route path="brands/:brandId/analytics" element={<AnalyticsPage />} />
                    
                    {/* Settings routes */}
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="brands/:brandId/settings" element={<SettingsPage />} />

                    {/* Partner network routes */}
                    <Route path="partners" element={<PartnersPage />} />
                    <Route path="partners/new" element={<AddPartnerPage />} />
                    <Route path="partners/:partnerId" element={<PartnerDetailPage />} />
                    <Route path="partners/applications/:applicationId" element={<PartnerApplicationDetailPage />} />
                    <Route path="wholesale-suppliers" element={<WholesaleSuppliersPage />} />
                    <Route path="production-partners" element={<ProductionPartnersPage />} />

                    {/* RFQ routes */}
                    <Route path="rfqs" element={<RFQListPage />} />
                    <Route path="rfqs/new" element={<CreateRFQPage />} />
                    <Route path="rfqs/:rfqId" element={<RFQDetailPage />} />

                    {/* Customer measurements */}
                    <Route path="customers/measurements" element={<MeasurementProfilesPage />} />
                    {/* 404 route */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Route>
                </Routes>
                
                {/* Toast notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 5000,
                    style: {
                      background: '#333',
                      color: '#fff',
                      borderRadius: '6px',
                    },
                    success: {
                      style: {
                        background: '#4caf50',
                      },
                    },
                    error: {
                      style: {
                        background: '#f44336',
                      },
                    },
                  }}
                />
              </div>
            </Router>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
